import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import TabNavigation from '../../components/ui/TabNavigation';
import Core4MetricCard from './components/Core4MetricCard';
import OverallSummaryCard from './components/OverallSummaryCard';
import DetailedAnalyticsCard from './components/DetailedAnalyticsCard';
import QuickActionCard from './components/QuickActionCard';

const StatsPage = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock user data
  const mockUser = {
    name: "Sarah Johnson",
    role: "client",
    startDate: "2024-08-15",
    currentDay: 41
  };

  // Mock stats data
  const mockStatsData = {
    core4Stats: {
      Faith: {
        completedCount: 28,
        loggedDays: 35,
        percentage: 80.0,
        sparklineData: [1, 0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1],
        currentStreak: 4
      },
      Family: {
        completedCount: 31,
        loggedDays: 35,
        percentage: 88.6,
        sparklineData: [1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0],
        currentStreak: 2
      },
      Finances: {
        completedCount: 22,
        loggedDays: 35,
        percentage: 62.9,
        sparklineData: [0, 1, 0, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1],
        currentStreak: 0
      },
      Fitness: {
        completedCount: 26,
        loggedDays: 35,
        percentage: 74.3,
        sparklineData: [1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 0, 1, 1, 1],
        currentStreak: 3
      }
    },
    overallStats: {
      totalLoggedDays: 35,
      overallCompletionRate: 76.4,
      programStartDate: "2024-08-15",
      currentDay: 41
    },
    weeklyData: [
      {
        date: "Sep 18",
        completed: { Faith: true, Family: true, Finances: false, Fitness: true }
      },
      {
        date: "Sep 19",
        completed: { Faith: true, Family: true, Finances: true, Fitness: true }
      },
      {
        date: "Sep 20",
        completed: { Faith: false, Family: true, Finances: true, Fitness: false }
      },
      {
        date: "Sep 21",
        completed: { Faith: true, Family: true, Finances: false, Fitness: true }
      },
      {
        date: "Sep 22",
        completed: { Faith: true, Family: false, Finances: true, Fitness: true }
      },
      {
        date: "Sep 23",
        completed: { Faith: true, Family: true, Finances: true, Fitness: true }
      },
      {
        date: "Sep 24",
        completed: { Faith: true, Family: true, Finances: false, Fitness: true }
      }
    ],
    monthlyTrends: {
      Faith: { average: 80.0, trend: 5.2 },
      Family: { average: 88.6, trend: 2.1 },
      Finances: { average: 62.9, trend: -3.4 },
      Fitness: { average: 74.3, trend: 8.7 }
    },
    bestPerformingArea: "Family",
    improvementSuggestions: [
      "Consider setting smaller, more achievable financial goals to build momentum",
      "Try scheduling fitness activities at consistent times each day",
      "Review your faith practices - morning routines show higher completion rates"
    ]
  };

  useEffect(() => {
    // Simulate loading stats data
    const loadStatsData = async () => {
      setLoading(true);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      setStatsData(mockStatsData);
      setLoading(false);
    };

    loadStatsData();
  }, []);

  const core4Areas = [
    { key: 'Faith', icon: 'Heart', color: 'primary' },
    { key: 'Family', icon: 'Users', color: 'secondary' },
    { key: 'Finances', icon: 'DollarSign', color: 'accent' },
    { key: 'Fitness', icon: 'Activity', color: 'success' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Stats - Aletheos Daily</title>
          <meta name="description" content="Track your CORE4 progress and analytics" />
        </Helmet>
        <AuthenticatedHeader 
          userRole={mockUser?.role}
          userName={mockUser?.name}
        />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-muted-foreground">Loading your progress statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Stats - Aletheos Daily</title>
        <meta name="description" content="Track your CORE4 progress and analytics" />
      </Helmet>
      <AuthenticatedHeader 
        userRole={mockUser?.role}
        userName={mockUser?.name}
      />
      <main className="pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
              Progress Statistics
            </h1>
            <p className="text-muted-foreground">
              Track your CORE4 completion rates and personal development journey
            </p>
          </div>

          {/* Overall Summary */}
          <div className="mb-8">
            <OverallSummaryCard
              totalLoggedDays={statsData?.overallStats?.totalLoggedDays}
              overallCompletionRate={statsData?.overallStats?.overallCompletionRate}
              programStartDate={statsData?.overallStats?.programStartDate}
              currentDay={statsData?.overallStats?.currentDay}
            />
          </div>

          {/* CORE4 Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {core4Areas?.map((area) => {
              const areaStats = statsData?.core4Stats?.[area?.key];
              return (
                <Core4MetricCard
                  key={area?.key}
                  title={area?.key}
                  icon={area?.icon}
                  completedCount={areaStats?.completedCount}
                  loggedDays={areaStats?.loggedDays}
                  percentage={areaStats?.percentage}
                  color={area?.color}
                  sparklineData={areaStats?.sparklineData}
                  currentStreak={areaStats?.currentStreak}
                  showSparkline={true}
                  showStreak={true}
                />
              );
            })}
          </div>

          {/* Detailed Analytics and Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DetailedAnalyticsCard
                weeklyData={statsData?.weeklyData}
                monthlyTrends={statsData?.monthlyTrends}
                bestPerformingArea={statsData?.bestPerformingArea}
                improvementSuggestions={statsData?.improvementSuggestions}
              />
            </div>
            <div className="lg:col-span-1">
              <QuickActionCard />
            </div>
          </div>
        </div>
      </main>
      {/* Mobile Navigation */}
      <TabNavigation />
    </div>
  );
};

export default StatsPage;